import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { getDefaultGlobeLocations } from "@core/services/auth_services";
import { lazyComponent } from "../../../../lazy";
// three.js and friends stay in their own chunk; loaded only for globe backgrounds
const LazyGlobeBackground = lazyComponent(() => import(/* webpackChunkName: "globe" */ './GlobeBackground'));
function isWebGLAvailable() {
    try {
        const canvas = document.createElement('canvas');
        return !!(canvas.getContext('webgl2') || canvas.getContext('webgl'));
    }
    catch (_a) {
        return false;
    }
}
/**
 * The globe is decoration — if it fails for any reason (no WebGL, driver
 * issues), the login form must keep working on the fallback background.
 */
class GlobeErrorBoundary extends React.Component {
    constructor() {
        super(...arguments);
        this.state = { failed: false };
    }
    static getDerivedStateFromError() {
        return { failed: true };
    }
    componentDidCatch(error) {
        console.warn('Globe background failed to initialise; keeping fallback background.', error);
    }
    render() {
        return this.state.failed ? null : this.props.children;
    }
}
/**
 * Defers the globe chunk's dynamic import until after the auth form's first
 * paint (same pattern as dashboard/BackgroundRenderer) so the login page
 * stays instant. Skips the chunk entirely when WebGL isn't available.
 */
const DeferredGlobe = (props) => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        const id = requestAnimationFrame(() => setMounted(true));
        return () => cancelAnimationFrame(id);
    }, []);
    if (!mounted || !isWebGLAvailable())
        return null;
    return (_jsx(GlobeErrorBoundary, { children: _jsx(LazyGlobeBackground, Object.assign({}, props)) }));
};
/**
 * AuthLayout - Common layout wrapper for all authentication pages
 *
 * Responsibilities:
 * - Apply page background (color/image)
 * - Set CSS variables for auth theme colors at document root level
 * - Render children (form content)
 *
 * Does NOT handle form positioning - that's the responsibility of individual form components
 */
const AuthLayout = ({ children, config }) => {
    // Apply CSS variables for theming at document root (same pattern as main app)
    useEffect(() => {
        if (!config)
            return;
        const rootElement = document.documentElement;
        const { loginForm } = config;
        // Apply common form controls styles (shared across all layouts)
        const formControls = loginForm.formControls;
        rootElement.style.setProperty('--auth-input-bg', formControls.input.bg);
        rootElement.style.setProperty('--auth-input-text', formControls.input.textColor);
        rootElement.style.setProperty('--auth-input-border', formControls.input.border);
        rootElement.style.setProperty('--auth-input-border-focus', formControls.input.borderFocus);
        rootElement.style.setProperty('--auth-input-border-radius', formControls.input.borderRadius);
        rootElement.style.setProperty('--auth-btn-bg', formControls.button.bg);
        rootElement.style.setProperty('--auth-btn-text', formControls.button.textColor);
        rootElement.style.setProperty('--auth-btn-border', formControls.button.border);
        rootElement.style.setProperty('--auth-btn-bg-hover', formControls.button.bgHover);
        rootElement.style.setProperty('--auth-btn-text-hover', formControls.button.textColorHover);
        rootElement.style.setProperty('--auth-btn-border-hover', formControls.button.borderHover);
        rootElement.style.setProperty('--auth-btn-bg-disabled', formControls.button.bgDisabled);
        rootElement.style.setProperty('--auth-btn-text-disabled', formControls.button.textColorDisabled);
        rootElement.style.setProperty('--auth-btn-border-radius', formControls.button.borderRadius);
        rootElement.style.setProperty('--auth-link-color', formControls.link.color);
        rootElement.style.setProperty('--auth-link-color-hover', formControls.link.colorHover);
        // Apply layout-specific CSS variables
        if (loginForm.type === 'island-horizontal' && loginForm.islandHorizontal) {
            const cfg = loginForm.islandHorizontal;
            rootElement.style.setProperty('--auth-island-max-width', cfg.maxWidth);
            rootElement.style.setProperty('--auth-island-padding', cfg.padding);
            rootElement.style.setProperty('--auth-island-border-radius', cfg.borderRadius);
            rootElement.style.setProperty('--auth-island-border-width', cfg.border.width);
            rootElement.style.setProperty('--auth-island-border-color', cfg.border.color);
            rootElement.style.setProperty('--auth-island-shadow', cfg.shadow);
            rootElement.style.setProperty('--auth-island-bg', cfg.bg);
            rootElement.style.setProperty('--auth-island-text-color', cfg.textColor);
            rootElement.style.setProperty('--auth-island-spacing-x', cfg.spacing.x);
            rootElement.style.setProperty('--auth-island-spacing-y', cfg.spacing.y);
        }
        if (loginForm.type === 'island-vertical' && loginForm.islandVertical) {
            const cfg = loginForm.islandVertical;
            rootElement.style.setProperty('--auth-island-max-width', cfg.maxWidth);
            rootElement.style.setProperty('--auth-island-padding', cfg.padding);
            rootElement.style.setProperty('--auth-island-border-radius', cfg.borderRadius);
            rootElement.style.setProperty('--auth-island-border-width', cfg.border.width);
            rootElement.style.setProperty('--auth-island-border-color', cfg.border.color);
            rootElement.style.setProperty('--auth-island-shadow', cfg.shadow);
            rootElement.style.setProperty('--auth-island-bg', cfg.bg);
            rootElement.style.setProperty('--auth-island-gap', cfg.gap);
            rootElement.style.setProperty('--auth-island-spacing-x', cfg.spacing.x);
            rootElement.style.setProperty('--auth-island-spacing-y', cfg.spacing.y);
            rootElement.style.setProperty('--auth-island-left-width', cfg.leftPanel.width);
            rootElement.style.setProperty('--auth-island-left-bg', cfg.leftPanel.bg);
            rootElement.style.setProperty('--auth-island-left-text', cfg.leftPanel.textColor);
            rootElement.style.setProperty('--auth-island-left-padding', cfg.leftPanel.padding);
            rootElement.style.setProperty('--auth-island-right-width', cfg.rightPanel.width);
            rootElement.style.setProperty('--auth-island-right-bg', cfg.rightPanel.bg);
            rootElement.style.setProperty('--auth-island-right-text', cfg.rightPanel.textColor);
            rootElement.style.setProperty('--auth-island-right-padding', cfg.rightPanel.padding);
        }
        // Apply fullpage-specific CSS variables if configured
        if (loginForm.type === 'fullpage' && loginForm.fullpage) {
            const cfg = loginForm.fullpage;
            rootElement.style.setProperty('--auth-fullpage-left-width', cfg.leftPanel.width);
            rootElement.style.setProperty('--auth-fullpage-left-bg', cfg.leftPanel.bg);
            rootElement.style.setProperty('--auth-fullpage-left-text', cfg.leftPanel.textColor);
            rootElement.style.setProperty('--auth-fullpage-left-padding', cfg.leftPanel.padding);
            rootElement.style.setProperty('--auth-fullpage-right-width', cfg.rightPanel.width);
            rootElement.style.setProperty('--auth-fullpage-right-bg', cfg.rightPanel.bg);
            rootElement.style.setProperty('--auth-fullpage-right-text', cfg.rightPanel.textColor);
            rootElement.style.setProperty('--auth-fullpage-right-padding', cfg.rightPanel.padding);
        }
    }, [config]);
    // Determine background style
    const getBackgroundStyle = () => {
        if (!config)
            return {};
        const { background } = config.layout;
        if (background.type === 'image') {
            return {
                backgroundImage: `url(${background.value})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            };
        }
        else {
            // 'color' and 'globe' both paint value as CSS background —
            // for 'globe' it's the instant fallback while the globe chunk loads
            return {
                background: background.value
            };
        }
    };
    const globeConfig = config === null || config === void 0 ? void 0 : config.layout.background.globe;
    const globeLocations = globeConfig === null || globeConfig === void 0 ? void 0 : globeConfig.locations;
    return (_jsxs("div", { className: "uxpcore_authlayout__container", style: getBackgroundStyle(), children: [(config === null || config === void 0 ? void 0 : config.layout.customStyles) && (_jsx("style", { children: config.layout.customStyles })), (config === null || config === void 0 ? void 0 : config.layout.background.type) === 'globe' && (_jsx(DeferredGlobe, { locations: (globeLocations === null || globeLocations === void 0 ? void 0 : globeLocations.length) ? globeLocations : getDefaultGlobeLocations(), align: globeConfig === null || globeConfig === void 0 ? void 0 : globeConfig.align, size: globeConfig === null || globeConfig === void 0 ? void 0 : globeConfig.size })), children] }));
};
export default AuthLayout;
